import React, { useCallback, useMemo, useState } from "react";
import { fetchQuery, graphql } from "react-relay";
import {
	components,
	type DropdownIndicatorProps,
	type GroupHeadingProps,
	type GroupProps,
	type InputActionMeta,
	type MenuPlacement,
	type OnChangeValue,
	type OptionProps,
	type SingleValueProps,
	type StylesConfig,
} from "react-select";
import AsyncSelect from "react-select/async";

import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce } from "lodash";

import { CycleCategory, getCycleCategory } from "~/account/Cycles/utils";
import { environment } from "~/models";

import type {
	ObjectiveListInputQuery,
	ObjectiveListInputQuery$data,
} from "~/__generated__/ObjectiveListInputQuery.graphql";

import dropdownStyles from "~/common/css/Dropdown.module.css";

import styles from "./ObjectiveListInput.module.css";

const parentsQuery = graphql`
	query ObjectiveListInputQuery($node: ID, $title: String) {
		objectivesParentSearch(forNode: $node, title: $title) {
			id
			title
			level
			cycle {
				startDate
				endDate
			}
		}
	}
`;

export type ObjectiveType = NonNullable<ObjectiveListInputQuery$data["objectivesParentSearch"]>[0];

export interface GroupedParentObjectivesOption {
	readonly label: CycleCategory;
	readonly options: readonly ObjectiveType[];
}

function DropdownIndicator(props: DropdownIndicatorProps<ObjectiveType, false, GroupedParentObjectivesOption>) {
	return (
		<div
			{...props.innerProps}
			className={cx(dropdownStyles.caret, {
				[dropdownStyles.caretOpen]: props.selectProps.menuIsOpen,
				[dropdownStyles.disabled]: props.isDisabled,
			})}
		>
			<FontAwesomeIcon icon={faCaretDown} />
		</div>
	);
}

function SingleValue(props: SingleValueProps<ObjectiveType, false, GroupedParentObjectivesOption>) {
	const objective = props.data as ObjectiveType;
	const RSSingleValue = components.SingleValue;

	if (!objective?.id) {
		return null;
	}

	const { title } = objective;

	return (
		<RSSingleValue className={cx({ [styles.untitled]: !title })} {...props}>
			{title || "Untitled Objective"}
		</RSSingleValue>
	);
}

function Group(props: GroupProps<ObjectiveType, false, GroupedParentObjectivesOption>) {
	return <components.Group {...props} className={dropdownStyles.group} />;
}

function GroupHeading(props: GroupHeadingProps<ObjectiveType, false, GroupedParentObjectivesOption>) {
	return <components.GroupHeading {...props} className={dropdownStyles.groupHeading} />;
}

interface ObjectiveListInputProps {
	selected: ObjectiveType | null;
	// Used if we are adding a parent for this objective
	// So we don't include this objective and childrens
	parentingObjective: string;
	onChange: (parent: ObjectiveType | null) => void;
	className?: string;
	disabled?: boolean;
	menuPlacement?: MenuPlacement;
}

export default function ObjectiveListInput({
	selected,
	parentingObjective,
	onChange,
	className,
	disabled,
	menuPlacement,
}: ObjectiveListInputProps) {
	const [inputValue, setInputValue] = useState<string>("");

	const selectObjective = useCallback(
		(newParent: OnChangeValue<ObjectiveType, false>) => {
			if (!newParent?.id) {
				onChange(null);
			} else {
				onChange(newParent);
			}
			setInputValue("");
		},
		[onChange],
	);

	const onInputChange = useCallback(
		(val: string, meta: InputActionMeta) => {
			if (meta.action === "input-change") {
				setInputValue(val);
			}
		},
		[setInputValue],
	);

	const loadOptions = useCallback(
		(title: string, callback) => {
			fetchQuery<ObjectiveListInputQuery>(
				environment,
				parentsQuery,
				{ node: parentingObjective, title },
				{ fetchPolicy: "network-only" },
			)
				.toPromise()
				.then(response => {
					let options: Array<ObjectiveType> = [];
					let groupedOptions: Array<GroupedParentObjectivesOption> = [];
					if (response?.objectivesParentSearch) {
						options = options.concat(response.objectivesParentSearch);
						groupedOptions = createGroupedOptions(options);
					}
					callback(groupedOptions);
				});
		},
		[parentingObjective],
	);

	const createGroupedOptions = (objectives: ObjectiveType[]) => {
		const objectivesGroups: GroupedParentObjectivesOption[] = [];
		const dict = new Map<CycleCategory, ObjectiveType[]>([
			[CycleCategory.CURRENT_CYCLE, []],
			[CycleCategory.UPCOMING_CYCLE, []],
			[CycleCategory.PAST_CYCLE, []],
		]);

		objectives.forEach(objective => {
			if (!objective.cycle) {
				return;
			}

			const { startDate, endDate } = objective.cycle;

			const cycleCategory: CycleCategory = getCycleCategory(startDate, endDate);

			if (cycleCategory === CycleCategory.CURRENT_CYCLE) {
				dict.set(CycleCategory.CURRENT_CYCLE, dict.get(CycleCategory.CURRENT_CYCLE)?.concat(objective) || []);
			} else if (cycleCategory === CycleCategory.UPCOMING_CYCLE) {
				dict.set(CycleCategory.UPCOMING_CYCLE, dict.get(CycleCategory.UPCOMING_CYCLE)?.concat(objective) || []);
			} else if (cycleCategory === CycleCategory.PAST_CYCLE) {
				dict.set(CycleCategory.PAST_CYCLE, dict.get(CycleCategory.PAST_CYCLE)?.concat(objective) || []);
			}
		});

		for (const [key, value] of dict) {
			objectivesGroups.push({
				label: key,
				options: value,
			});
		}

		return objectivesGroups;
	};

	const Option = (props: OptionProps<ObjectiveType, false, GroupedParentObjectivesOption>) => {
		const { title, level } = props.data as ObjectiveType;

		return (
			<div className={cx(dropdownStyles.option, { [dropdownStyles.untitled]: !title })} {...props.innerProps}>
				{!inputValue && level > 0 && (
					<>
						<span style={{ width: `${15 * (level - 1)}px`, height: "1px", display: "inline-block" }} />
						{"∟ "}
					</>
				)}
				{title || "Untitled Objective"}
			</div>
		);
	};

	const debounceLoadOptions = useMemo(() => debounce(loadOptions, 500), [loadOptions]);

	const getOptionValue = useCallback((objective: ObjectiveType) => objective.id, []);

	const customStyles: StylesConfig<ObjectiveType, false, GroupedParentObjectivesOption> = {
		control: () => {
			return {
				display: "flex",
				height: "34.78px",
				border: "1px solid var(--inputBorderColor)",
				borderRadius: "6px",
				cursor: "pointer",
				"&:hover > div:last-child": {
					background: "var(--veryMutedGrey)",
					color: "initial",
					borderTopRightRadius: "4px",
					borderBottomRightRadius: "4px",
				},
				"&> div:last-child": {
					background: "initial",
					color: "initial",
					transform: "rotate(0)",
				},
			};
		},
		menu: base => ({
			...base,
			background: "var(--flyoutBackgroundColor)",
			boxShadow: "var(--flyoutBoxShadow)",
			maxHeight: "225px",
			textAlign: "start",
			padding: "0.25rem 0.25rem",
		}),
		menuList: base => ({
			...base,
			maxHeight: "225px",
		}),
		dropdownIndicator: base => ({
			...base,
			cursor: "pointer",
		}),
		input: base => ({
			...base,
			color: "var(--primaryTextColor)",
		}),
		placeholder: base => ({
			...base,
		}),
		valueContainer: base => ({
			...base,
		}),
		singleValue: base => ({
			...base,
			color: "var(--primaryTextColor)",
		}),
		group: () => ({}),
		groupHeading: () => ({}),
	};

	return (
		<AsyncSelect<ObjectiveType, false, GroupedParentObjectivesOption>
			defaultOptions
			isSearchable
			isClearable
			value={selected}
			onChange={selectObjective}
			inputValue={inputValue}
			onInputChange={onInputChange}
			loadOptions={debounceLoadOptions}
			getOptionValue={getOptionValue}
			components={{ IndicatorSeparator: null, Option, DropdownIndicator, SingleValue, Group, GroupHeading }}
			placeholder="Select a parent objective"
			styles={customStyles}
			isDisabled={disabled}
			menuPosition="absolute"
			menuPlacement={menuPlacement}
			className={cx(dropdownStyles.input, className, { [dropdownStyles.disabled]: disabled })}
		/>
	);
}
